/**
 * EXPERT ROOMS (EXPERT-ROOMS.md §4, E8 X1) — deploy an Expert Network as a TEAM OF ROOMS: the Lead room, then one
 * LINKED room per Team (department), each its own room release (a Lead's bootstrap alone is seconds — N rooms in one
 * request would not fit an API call). Resumable: an existing deployment's Lead is reused and only missing Teams get a
 * room. The server holds the rules (one deployment per workspace, a department must match its Lead + Team).
 */
import { releaseNetwork } from './agenticNetworksApi';

/** PURE — the room name for a Team's department room: ``{lead}-{team}`` (letters / digits / dashes, ≤ 40). */
export function departmentRoomName(leadPoolId, team) {
  const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const t = slug(team) || 'team';
  const lead = slug(leadPoolId).slice(0, Math.max(3, 39 - t.length));
  return `${lead}-${t}`.slice(0, 40).replace(/-+$/g, '');
}

/** PURE — a stage team's Lead room name (EXPERT-ROOMS §15): ``lily`` → ``lily-dev`` / ``lily-qa`` / ``lily`` (prod —
 *  the released one has no tag). Any stage suffix already on the base is replaced, never doubled. */
export function stageLeadName(base, stage) {
  const b = String(base || '').replace(/-(dev|qa)$/i, '') || 'expert';
  const st = String(stage || 'dev').toLowerCase();
  return st === 'prod' ? b : `${b}-${st === 'qa' ? 'qa' : 'dev'}`;
}

/** PURE — the network release a stage's training release was cut on (0 = unknown → the dialog's own pick). */
export function stageNetworkRelease(network, stage) {
  const st = ((network?.expertReleases || {}).stages || []).find((x) => x.stage === stage);
  return Number(st?.networkRelease || 0);
}

/** PURE — the Teams still without a room. */
export function missingTeams(deployment) {
  const d = deployment || {};
  const have = d.departments || {};
  return (d.teams || []).filter((t) => !have[t]);
}

/**
 * @param {string} networkId
 * ``base`` = the dialog's other room settings (owner, supervisors, trigger, connections…) — every room of the team
 * is created with them.
 * @param {{ leadName?: string, deployment?: object|null, versionRef?: string, base?: object,
 *           release?: Function, onProgress?: (step: {team: string, status: string, message?: string}) => void }} opts
 * @returns {Promise<{deployment: object, failed: Array<{team: string, message: string}>}>}
 */
export async function deployExpertTeam(networkId, opts = {}) {
  const release = opts.release || releaseNetwork;
  const report = (s) => { try { opts.onProgress?.(s); } catch { /* ignore */ } };
  let dep = opts.deployment && opts.deployment.leadPoolId ? opts.deployment : null;
  if (!dep) {
    report({ team: '', status: 'creating' });
    const res = await release(networkId, {
      ...(opts.base || {}), mode: 'NEW_POOL', poolName: opts.leadName, ...(opts.versionRef ? { versionRef: opts.versionRef } : {}),
      expertDeployment: { role: 'lead', ...(opts.stage ? { stage: opts.stage } : {}) },
    });
    if (res && res.noop === true) {
      throw new Error(res.message || 'No changes since the last release — pick the existing version above, then deploy the team.');
    }
    dep = res?.expertDeployment;
    if (!dep?.leadPoolId) throw new Error('The Lead room was created but the deployment was not recorded.');
    report({ team: '', status: 'done' });
  }
  const versionRef = dep.release ? `v${dep.release}` : (opts.versionRef || '');
  const failed = [];
  for (const team of missingTeams(dep)) {
    report({ team, status: 'creating' });
    try {
      const res = await release(networkId, {
        ...(opts.base || {}), mode: 'NEW_POOL', poolName: departmentRoomName(dep.leadPoolId, team), roomKind: 'linked',
        leadPoolId: dep.leadPoolId, ...(versionRef ? { versionRef } : {}),
        expertDeployment: { role: 'department', team, ...(opts.stage ? { stage: opts.stage } : {}) },
      });
      if (res?.expertDeployment) dep = res.expertDeployment;
      report({ team, status: 'done' });
    } catch (e) {
      failed.push({ team, message: e?.message || 'failed' });
      report({ team, status: 'failed', message: e?.message || 'failed' });
    }
  }
  return { deployment: dep, failed };
}
